// src/components/Sidebar.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Home, User, Newspaper, BookOpen, Compass,
  UserPlus, LogIn, LogOut, Microscope, CreditCard,
  PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import API from "../utils/axiosInstance";
import { isLoggedIn as getLogged, setLoggedOut } from "../utils/auth";
import "./Sidebar.css";
import LanguageSwitch from "./LanguageSwitch";
import { useTranslation } from "react-i18next";

// Grup 1: her zaman görünen ana gezinme öğeleri (giriş durumu fark etmez).
const primaryItems = [
  { nameKey: "nav.home",  icon: <Home size={20} />, path: "/" },
  { nameKey: "nav.events", icon: <BookOpen size={20} />, path: "/events" },
  { nameKey: "nav.news", icon: <Newspaper size={20} />, path: "/news" },
  { nameKey: "nav.social", icon: <Compass size={20} />, path: "/social" },
  { nameKey: "nav.analyze", icon: <Microscope size={20} />, path: "/analyze" },
];

// Grup 2: hesapla ilgili öğeler — bir ayırıcının altında ayrı bir küme
// olarak gösteriliyor. auth/guest filtresi giriş durumuna göre hangisinin
// göründüğünü belirliyor (bkz. filteredSecondary).
const secondaryItems = [
  { nameKey: "nav.profile", icon: <User size={20} />, path: "/profile", auth: true },
  { nameKey: "nav.buyCredits", icon: <CreditCard size={20} />, path: "/buy-credits", auth: true },
  { nameKey: "nav.login", icon: <LogIn size={20} />, path: "/login", guest: true },
  { nameKey: "nav.register", icon: <UserPlus size={20} />, path: "/register", guest: true }
];

function ToothIcon(props) {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false" {...props}>
      <path
        d="M8.1 4.5c2.25-.92 4.05.1 5.42.74.95.45 1.7.8 2.48.8s1.53-.35 2.48-.8c1.37-.64 3.17-1.66 5.42-.74 2.88 1.18 4.38 3.83 4.1 7.22-.23 2.75-1.35 5.12-2.5 7.36-.8 1.56-1.24 3.25-1.62 4.84-.55 2.28-1.15 4.78-3.04 4.78-1.87 0-2.55-2.4-3.08-4.3-.43-1.56-.84-3.02-1.76-3.02s-1.33 1.46-1.76 3.02c-.53 1.9-1.21 4.3-3.08 4.3-1.9 0-2.5-2.5-3.04-4.78-.38-1.59-.82-3.28-1.62-4.84-1.15-2.24-2.27-4.61-2.5-7.36-.28-3.39 1.22-6.04 4.1-7.22Z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.4 5.7c1.28.78 2.34 1.14 3.6 1.14 1.26 0 2.32-.36 3.6-1.14"
        stroke="currentColor"
        strokeWidth="1.55"
        strokeLinecap="round"
        opacity=".72"
      />
    </svg>
  );
}

export default function Sidebar({ collapsed = false, setCollapsed = () => {} }) {
  const location = useLocation();
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(getLogged());
  const [tooltip, setTooltip] = useState(null); // { label, top, left }
  const headerRef = useRef(null);
  const langWrapRef = useRef(null);
  const navGroupRef = useRef(null);

  // Collapsed haldeki ikon-only linkler için tooltip: .sidebar artık
  // overflow-x:clip olduğundan (bkz. Sidebar.css) eski absolute-escape
  // yöntemi çalışmıyor — bunun yerine hover edilen linkin GERÇEK ekran
  // koordinatı okunup position:fixed bir tooltip render ediliyor (fixed
  // elemanlar ebeveynin overflow kırpmasından etkilenmez).
  const showTooltip = (e, label) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTooltip({ label, top: r.top + r.height / 2, left: r.right + 10 });
  };
  const hideTooltip = () => setTooltip(null);

  // KÖK NEDEN (ör. "Güncel" tooltip'i sayfa içeriğinin üzerinde asılı
  // kalıyor): Sidebar App.js'te Routes'un dışında, hep mount'lu duruyor —
  // bir linke hover edip sonra o linke TIKLAYARAK başka bir sayfaya
  // geçildiğinde, link DOM'dan kalkmadan/mouse gerçekten ayrılmadan route
  // değişebiliyor ve onMouseLeave hiç tetiklenmeyebiliyor. Sonuç: bir
  // önceki sayfadaki linkin tooltip'i, position:fixed olduğu için yeni
  // sayfanın içeriğinin üzerinde asılı kalıyordu. Her route değişiminde
  // tooltip'i kesin olarak kapatıyoruz.
  useEffect(() => {
    setTooltip(null);
  }, [location.pathname]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "isLoggedIn") setIsLoggedIn(e.newValue === "1");
    };
    const onAuthChange = () => setIsLoggedIn(getLogged());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth:change", onAuthChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:change", onAuthChange);
    };
  }, []);

  const filteredSecondary = secondaryItems.filter((item) => {
    if (item.auth && !isLoggedIn) return false;
    if (item.guest && isLoggedIn) return false;
    return true;
  });

  // Hero (ve ileride ihtiyaç duyabilecek başka bileşenler) sidebar'ın
  // GERÇEK İÇERİK yüksekliğini bilsin diye bunu ölçüp global bir CSS
  // custom property olarak yayınlıyoruz. Bilerek navGroupRef'in EKRANDAKİ
  // pozisyonu (getBoundingClientRect().bottom) DEĞİL, header + dil bloğu +
  // menü öğelerinin kendi yüksekliklerinin TOPLAMI kullanılıyor — çünkü
  // nav grubundan sonraki .sidebar-spacer (bkz. Sidebar.css) kalan boş
  // alanı viewport yüksekliğine göre dolduruyor, bu da navGroupRef'in
  // ekrandaki "bottom" konumunu içerik hiç değişmese bile kaydırabiliyordu.
  // Öğe aralarındaki gap'ler (.sidebar-menu'nün kendi gap'i) bilinçli
  // olarak toplama DAHİL EDİLMİYOR — sadece linklerin kendi
  // offsetHeight'ları toplanıyor.
  useLayoutEffect(() => {
    const measure = () => {
      if (!navGroupRef.current) return;
      const headerH = headerRef.current?.getBoundingClientRect().height || 56;
      const langH = langWrapRef.current?.getBoundingClientRect().height || 0;
      const items = navGroupRef.current.querySelectorAll(".sidebar-link");
      const itemsSum = Array.from(items).reduce(
        (sum, el) => sum + el.getBoundingClientRect().height,
        0
      );
      const total = headerH + langH + itemsSum;
      document.documentElement.style.setProperty(
        "--sidebar-content-height",
        `${Math.round(total)}px`
      );
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [filteredSecondary.length, collapsed, isLoggedIn]);

  const handleLogout = async () => {
    try { await API.post("/api/auth/logout/"); } catch {}
    setLoggedOut();
    window.location.replace("/login");
  };

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-header-wrapper" ref={headerRef}>
        <div className="sidebar-brand">
          <ToothIcon className="brand-icon" />
          {!collapsed && (
            <div className="brand-text-block">
              <span className="brand-text" aria-label="IMPLAGO logotype">
                IMPLAGO
              </span>
              <span className="brand-subtitle">{t("nav.brandSubtitle")}</span>
            </div>
          )}
        </div>

        <button
          className="sidebar-toggle-inside"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? t("nav.expandMenu") : t("nav.collapseMenu")}
          aria-pressed={collapsed}
        >
          {collapsed ? <PanelLeftOpen size={20} strokeWidth={2.2} /> : <PanelLeftClose size={20} strokeWidth={2.2} />}
        </button>
      </div>

      <div className="sidebar-lang-wrapper" ref={langWrapRef}>
        <LanguageSwitch collapsed={collapsed} />
      </div>

      <div className="sidebar-nav-group" ref={navGroupRef}>
        <nav className="sidebar-menu sidebar-menu--primary">
          {primaryItems.map((item, idx) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            const label = t(item.nameKey);
            return (
              <Link
                key={idx}
                to={item.path}
                className={`sidebar-link ${isActive ? "active" : ""}`}
                aria-current={isActive ? "page" : undefined}
                onMouseEnter={(e) => showTooltip(e, label)}
                onMouseLeave={hideTooltip}
                onFocus={(e) => showTooltip(e, label)}
                onBlur={hideTooltip}
              >
                <div className="icon">{item.icon}</div>
                <span className="label">{label}</span>
              </Link>
            );
          })}
        </nav>

        {/* İkinci grup: hesapla ilgili öğeler (giriş yapılmışsa Profil/
            Kredi Satın Al/Çıkış Yap; yapılmamışsa Giriş Yap/Kayıt Ol).
            "HESAP" etiketi SADECE giriş yapılmış durumda gösteriliyor. */}
        <div className="sidebar-group-divider">
          <span className="sidebar-group-divider__line" />
          {isLoggedIn && !collapsed && (
            <span className="sidebar-group-label">{t("nav.accountGroup")}</span>
          )}
        </div>

        <nav className="sidebar-menu sidebar-menu--secondary">
          {filteredSecondary.map((item, idx) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            const label = t(item.nameKey);
            return (
              <Link
                key={idx}
                to={item.path}
                className={`sidebar-link ${isActive ? "active" : ""}`}
                aria-current={isActive ? "page" : undefined}
                onMouseEnter={(e) => showTooltip(e, label)}
                onMouseLeave={hideTooltip}
                onFocus={(e) => showTooltip(e, label)}
                onBlur={hideTooltip}
              >
                <div className="icon">{item.icon}</div>
                <span className="label">{label}</span>
              </Link>
            );
          })}

          {isLoggedIn && (
            <button
              className="sidebar-link sidebar-link--logout"
              onClick={handleLogout}
              aria-label={t("nav.logout")}
              onMouseEnter={(e) => showTooltip(e, t("nav.logout"))}
              onMouseLeave={hideTooltip}
              onFocus={(e) => showTooltip(e, t("nav.logout"))}
              onBlur={hideTooltip}
            >
              <div className="icon"><LogOut size={20} /></div>
              <span className="label">{t("nav.logout")}</span>
            </button>
          )}
        </nav>
      </div>

      {/* Kalan alanı doldurup footer'ı en alta iten tek spacer. */}
      <div className="sidebar-spacer" aria-hidden="true" />

      <div className="sidebar-footer-area">
        <div className="sidebar-footer-divider" />
        {!collapsed && (
          <p className="sidebar-footer-copy">
            &copy; 2025 Implago <span className="sidebar-version">v1.0.0</span>
          </p>
        )}
      </div>

      {tooltip && (
        <div
          className="sidebar-tooltip"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.label}
        </div>
      )}
    </aside>
  );
}
